import type { RoutedKey } from "../../ui/input/router.ts";
import type { ActionId } from "./actions.ts";
import type { KeyTable } from "./page.ts";
import { actionPress } from "./press.ts";

/**
 * The scene's actions by the press (P1.9-T50; ADR-061's amendments "key rebinding", decisions 11 and 12, and "Move
 * joins the key table", point 3). The scene's key listener (`bindGameKeys`) asks on every press which handler takes
 * it, and the table answers as it stands then, so a rebound action answers its new key from the next press on, with
 * nothing bound again.
 */

/**
 * What an action does with a press; false when it did not take the key, which then does what it would have done.
 */
export type ActionHandler = (event: RoutedKey) => boolean | void;

/**
 * Finds the handler a press runs.
 */
export type PressHandler = (event: RoutedKey) => ActionHandler | undefined;

/**
 * The handler of the action a press is, by the table now.
 *
 * @param keys The key table.
 * @param handlers The handler of each action the caller answers; an action without one is left to its own reader.
 *   A press of Tab reaches the handler only while the game has focus (`press.ts` `actionPress`), and otherwise is
 *   left to move focus.
 * @returns A function that names a press's handler, or undefined when no handled action takes the press.
 * @example
 * bindGameKeys(scene, input, keyedActions(pageKeys, { interact: () => interact() }));
 */
export function keyedActions(keys: Pick<KeyTable, "actionOf">, handlers: Readonly<Partial<Record<ActionId, ActionHandler>>>): PressHandler {
  return (event) => {
    const id = keys.actionOf(event);
    return id === null || !actionPress(keys, id, event) ? undefined : handlers[id];
  };
}
